import type { ComponentPropsWithoutRef, ElementRef, HTMLAttributes } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Cross2Icon } from '@radix-ui/react-icons'
import { clsx } from 'clsx/lite'
import { forwardRef } from 'react'

const Root = Dialog.Root

const Trigger = Dialog.Trigger

const Close = Dialog.Close

const Portal = Dialog.Portal

const Overlay = forwardRef<
  ElementRef<typeof Dialog.Overlay>,
  ComponentPropsWithoutRef<typeof Dialog.Overlay>
>(({ className, ...props }, ref) => (
  <Dialog.Overlay
    className={clsx(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 absolute inset-0 z-[2147483647] bg-black/30',
      className
    )}
    {...props}
    ref={ref}
  />
))

type ContentProps = ComponentPropsWithoutRef<typeof Dialog.Content>

const Content = forwardRef<
  ElementRef<typeof Dialog.Content>,
  ContentProps
>(({ className, children, ...props }, ref) => (
  <Dialog.Content
    ref={ref}
    className={clsx(
      `bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] absolute left-[50%] top-[50%]
      z-[2147483647] grid max-w-lg translate-x-[-50%] translate-y-[-50%]
      gap-6 border p-6 shadow-lg duration-200 rounded-lg
      `,
      className
    )}
    {...props}
  >
    {children}
    <Dialog.Close className="absolute right-2 top-2 flex size-8 items-center justify-center hover:brightness-90">
      <Cross2Icon className="size-5" />
      <span className="sr-only">Close</span>
    </Dialog.Close>
  </Dialog.Content>
))

const Header = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={clsx(
      'flex flex-col space-y-2 text-center sm:text-left',
      className
    )}
    {...props}
  />
)

const Footer = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={clsx(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
)

const Title = forwardRef<
  ElementRef<typeof Dialog.Title>,
  ComponentPropsWithoutRef<typeof Dialog.Title>
>(({ className, ...props }, ref) => (
  <Dialog.Title
    ref={ref}
    className={clsx('text-foreground text-xl font-semibold', className)}
    {...props}
  />
))

const Description = forwardRef<
  ElementRef<typeof Dialog.Description>,
  ComponentPropsWithoutRef<typeof Dialog.Description>
>(({ className, ...props }, ref) => (
  <Dialog.Description
    ref={ref}
    className={clsx('text-muted-foreground', className)}
    {...props}
  />
))

export const Modal = {
  Root,
  Portal,
  Overlay,
  Trigger,
  Close,
  Content,
  Header,
  Footer,
  Title,
  Description,
}
